import { useEffect, useState } from 'react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import debounce from 'lodash/debounce'
import isNumber from 'lodash/isNumber'
import set from 'lodash/set'
import trim from 'lodash/trim'
import { observer } from 'mobx-react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { deleteGroupById } from 'API/groups'
import Button from 'components/Button'
import DeleteDialog from 'components/DeleteDialog'
import GlobalSpinner from 'components/GlobalSpinner'
import ModalDialog, { ModalDialogProps } from 'components/ModalDialog'
import TextField from 'components/TextField'
import { IGroup, IGroupMember, IGroupMemberPermissionId } from 'interfaces/group'
import { getValidArray } from 'utils/common'
import { shareProcessToGroups, shareProcessesToGroups } from '../../API/groupProcesses'
import { ITheme } from '../../interfaces/theme'
import { primary } from '../../theme/globalStyles'
import GroupOption from './components/GroupOption'
import styles from './shareWithGroupDialog.module.scss'

interface ShareProcessWithGroupsModalProps extends Omit<ModalDialogProps, 'headless'> {
  isEdit?: boolean
  selectedProcessList?: number[]
  refetch?: (organizationId: number) => void
  isBasicUser?: boolean
}

const ShareProcessWithGroupsModal = ({
  className,
  isEdit,
  selectedProcessList,
  refetch,
  isBasicUser,
  ...props
}: ShareProcessWithGroupsModalProps) => {
  const { groupStore, processStore, organizationStore, collectionStore, authStore } = useStores()
  const { userDetail } = authStore
  const { newProcessGroupIds, selectedProcessId, isGroupProcessesLoading } = processStore
  const { collection } = collectionStore
  const { organization } = organizationStore
  const { groups, groupMembers } = groupStore
  const currentTheme: ITheme = organization?.theme ?? {}
  const [selectedGroupIds, setSelectedGroupIds] = useState(selectedProcessList ? newProcessGroupIds : [])

  const [searchText, changeSearchText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [deleteGroupId, setDeleteGroupId] = useState(0)
  const filterGroup = {
    fields: ['name', 'id'],
    where: { organizationId: organization?.id ?? 0, name: { like: `.*${trim(searchText)}.*`, options: 'i' } }
  }
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const groupId = params.get('groupId') || ''

  useEffect(() => {
    if (!selectedProcessList) {
      processStore.setSelectedSharedGroupOfProcess(selectedProcessId)
    }
  }, [selectedProcessId])

  useEffect(() => {
    if (!selectedProcessList) {
      setSelectedGroupIds(newProcessGroupIds)
    }
  }, [newProcessGroupIds])

  useEffect(() => {
    if (groupId && isNumber(+groupId)) {
      processStore.setNewProcessGroupIds([+groupId])
      setSelectedGroupIds([+groupId])
    }
  }, [groupId])

  useEffect(() => {
    if (isEdit) {
      const collectionGroups = collection?.groups ?? []
      const groupIds = collectionGroups.map((group: IGroup) => group.id) || []
      const defaultGroupIds: number[] = []
      // eslint-disable-next-line array-callback-return
      groupIds.map(id => {
        if (isNumber(id)) {
          defaultGroupIds.push(id)
        }
      })
      setSelectedGroupIds(defaultGroupIds)
    }
  }, [isEdit])

  useEffect(() => {
    // API now handles permission filtering automatically
    groupStore.getGroups(filterGroup)
  }, [searchText])

  useEffect(() => {
    if (selectedProcessList) {
      setSelectedGroupIds([])
    }
  }, [selectedProcessList])

  function toggleGroup(id: number): void {
    if (selectedGroupIds.includes(id)) {
      const newGroupIds = selectedGroupIds.filter((selectedId: number) => selectedId !== id)
      setSelectedGroupIds(newGroupIds)
    } else {
      setSelectedGroupIds([...selectedGroupIds, id])
    }
  }

  function handleCancel(): void {
    !isEdit && setSelectedGroupIds([])
    props.toggle()
  }

  async function handleDeleteGroupConfirmed(): Promise<void> {
    if (deleteGroupId) {
      setDeleteGroupId(0)
      await deleteGroupById(deleteGroupId)
      groupStore.getGroups(filterGroup)
    }
  }

  const debounceSearchGroup = debounce(changeSearchText, 500)

  return (
    <ModalDialog headless className={cx(styles.shareGroupDialog, className)} {...props}>
      <div className="d-flex justify-content-between">
        <h4>Share with groups</h4>
      </div>
      <div className={styles.fieldWrapper}>
        <TextField
          placeholder="Search groups"
          onChange={(event: { currentTarget: { value: string } }) => {
            debounceSearchGroup(event.currentTarget.value)
          }}
        />
      </div>
      <div className={styles.groupList}>
        {isGroupProcessesLoading ? (
          <div className={styles.spinnerContainer}>
            <GlobalSpinner />
          </div>
        ) : (
          <>
            {Array.isArray(groups) &&
              groups.map(group => {
                const isSelected = selectedGroupIds.includes(Number(group?.id))
                return (
                  <GroupOption
                    key={group?.id}
                    isSelected={isSelected}
                    group={group}
                    toggleGroup={toggleGroup}
                    setDeleteGroupId={setDeleteGroupId}
                  />
                )
              })}
          </>
        )}
      </div>
      <div className={styles.actionBtns}>
        <Button color="light" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          color="info"
          backgroundColor={currentTheme?.primaryColor ?? primary}
          _hover={{ opacity: 0.8 }}
          _focus={{ opacity: 1 }}
          _active={{ opacity: 1 }}
          disabled={isLoading}
          onClick={async () => {
            setIsLoading(true)
            try {
              if (!selectedProcessList) {
                //  INFO Share to single group
                await shareProcessToGroups(selectedGroupIds, selectedProcessId)
              } else {
                // INFO Share to multiple groups
                await shareProcessesToGroups(selectedGroupIds, selectedProcessList)
              }
              toast.success('Share successfully')
              setSelectedGroupIds([])
              refetch && refetch(userDetail?.organizationId)
              props.toggle()
            } catch (error: any) {
              toast.error(`There was an error: ${error}`)
            } finally {
              setIsLoading(false)
            }
          }}
        >
          Save
        </Button>
      </div>
      <DeleteDialog
        isOpen={deleteGroupId > 0}
        message="Are you sure you want to DELETE this group ?"
        toggle={() => setDeleteGroupId(0)}
        onDelete={handleDeleteGroupConfirmed}
        onCancel={() => setDeleteGroupId(0)}
      />
    </ModalDialog>
  )
}

export default observer(ShareProcessWithGroupsModal)
